import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, ExternalLink, Loader2 } from 'lucide-react';
import { InfoCircle, Xmark } from 'iconoir-react';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { cn, copyTextToClipboard } from 'lib/utils';
import { useToast } from 'hooks/use-toast';
import {
  EMPTY_WHATSAPP_CONFIG,
  isWhatsAppConfigComplete,
  type WhatsAppConnectionConfig,
} from './whatsapp-config';

const META_DEVELOPER_URL = 'https://developers.facebook.com/apps/';

export type WhatsAppConfigureResult = {
  webhookUrl: string;
  connectionId: string;
};

type WhatsAppConfigModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (config: WhatsAppConnectionConfig) => Promise<WhatsAppConfigureResult | null | undefined>;
  onBindAndActivate?: (connectionId: string) => Promise<void>;
  isSaving?: boolean;
  isActivating?: boolean;
};

type SetupField = {
  key: keyof WhatsAppConnectionConfig;
  label: string;
  placeholder: string;
  secret?: boolean;
  compact?: boolean;
};

const inputClassName =
  'flex !h-11 w-full rounded-lg border border-grey-100 bg-white px-4 font-sans text-sm text-grey-700 shadow-none placeholder:text-grey-300 focus-visible:ring-1 focus-visible:ring-ring dark:border-border dark:bg-transparent dark:text-true-white dark:placeholder:text-grey-400';

const pillButtonClassName =
  'h-10 gap-1.5 rounded-[62px] border border-[#D9DBE9] px-4 text-sm font-medium text-[#170F49] shadow-none hover:opacity-90';

const pillButtonStyle = {
  backgroundImage: 'linear-gradient(151deg, #FFF -62.65%, #FBFBFE 83.01%)',
} as const;

export const WhatsAppConfigModal = ({
  open,
  onOpenChange,
  onSave,
  onBindAndActivate,
  isSaving = false,
  isActivating = false,
}: WhatsAppConfigModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState<WhatsAppConnectionConfig>(EMPTY_WHATSAPP_CONFIG);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const isBusy = isSaving || isActivating;
  const isAwaitingActivation = Boolean(webhookUrl && connectionId);

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_WHATSAPP_CONFIG);
      setWebhookUrl('');
      setConnectionId('');
    }
  }, [open]);

  const emphasisClass = 'font-semibold text-grey-700 dark:text-true-white';

  const steps = [
    {
      id: 'app',
      content: t('WHATSAPP_SHORT_STEP_1', {
        defaultValue: 'Create a Meta app with the WhatsApp product enabled.',
      }),
    },
    {
      id: 'waba',
      content: t('WHATSAPP_SHORT_STEP_2', {
        defaultValue: 'Select a WhatsApp Business Account and verify a business phone number.',
      }),
    },
    {
      id: 'copy',
      content: (
        <>
          {t('WHATSAPP_SHORT_STEP_3_PREFIX', { defaultValue: 'Copy IDs and tokens from' })}{' '}
          <span className={emphasisClass}>WhatsApp → API Setup</span>
          {'.'}
        </>
      ),
    },
    {
      id: 'save',
      content: (
        <>
          {t('WHATSAPP_SHORT_STEP_4_PREFIX', { defaultValue: 'Paste the values below and click' })}{' '}
          <span className={emphasisClass}>
            {t('SAVE_AND_VALIDATE', { defaultValue: 'Save and Validate' })}
          </span>
          {'.'}
        </>
      ),
    },
  ];

  const fields: SetupField[] = [
    {
      key: 'wabaId',
      label: t('WHATSAPP_WABA_ID', { defaultValue: 'WhatsApp Business Account ID' }),
      placeholder: t('WHATSAPP_WABA_ID_PLACEHOLDER', { defaultValue: 'From API Setup' }),
      compact: true,
    },
    {
      key: 'phoneNumberId',
      label: t('WHATSAPP_PHONE_NUMBER_ID', { defaultValue: 'Phone Number ID' }),
      placeholder: t('WHATSAPP_PHONE_NUMBER_ID_PLACEHOLDER', {
        defaultValue: 'Numeric ID, not the phone number',
      }),
      compact: true,
    },
    {
      key: 'accessToken',
      label: t('WHATSAPP_ACCESS_TOKEN', { defaultValue: 'Access Token' }),
      placeholder: t('WHATSAPP_ACCESS_TOKEN_PLACEHOLDER', { defaultValue: 'System User token' }),
      secret: true,
    },
    {
      key: 'appId',
      label: t('WHATSAPP_APP_ID', { defaultValue: 'Meta App ID' }),
      placeholder: t('WHATSAPP_APP_ID_PLACEHOLDER', { defaultValue: 'From App Settings → Basic' }),
      compact: true,
    },
    {
      key: 'appSecret',
      label: t('WHATSAPP_APP_SECRET', { defaultValue: 'Meta App Secret' }),
      placeholder: t('WHATSAPP_APP_SECRET_PLACEHOLDER', { defaultValue: 'App Settings → Basic' }),
      secret: true,
      compact: true,
    },
    {
      key: 'verifyToken',
      label: t('WHATSAPP_WEBHOOK_VERIFY_TOKEN', { defaultValue: 'Webhook Verify Token' }),
      placeholder: t('WHATSAPP_VERIFY_TOKEN_PLACEHOLDER', { defaultValue: 'Your private verify token' }),
      secret: true,
    },
  ];

  const wabaPhoneFields = fields.filter(
    (field) => field.key === 'wabaId' || field.key === 'phoneNumberId'
  );
  const metaAppFields = fields.filter((field) => field.key === 'appId' || field.key === 'appSecret');
  const accessTokenField = fields.find((field) => field.key === 'accessToken');
  const verifyTokenField = fields.find((field) => field.key === 'verifyToken');

  const handleClose = () => {
    if (isBusy) return;
    onOpenChange(false);
  };

  const updateField = (key: keyof WhatsAppConnectionConfig, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleCopyWebhookUrl = async () => {
    if (!webhookUrl) return;

    try {
      await copyTextToClipboard(webhookUrl);
      toast({
        variant: 'success',
        title: t('SUCCESS', { defaultValue: 'Success' }),
        description: t('WEBHOOK_URL_COPIED', { defaultValue: 'Webhook URL copied to clipboard.' }),
      });
    } catch {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description: t('FAILED_TO_COPY', { defaultValue: 'Failed to copy. Please try again.' }),
      });
    }
  };

  const handleSave = async () => {
    if (!isWhatsAppConfigComplete(form) || !onSave) return;

    try {
      const result = await onSave({
        appId: form.appId.trim(),
        wabaId: form.wabaId.trim(),
        phoneNumberId: form.phoneNumberId.trim(),
        accessToken: form.accessToken.trim(),
        appSecret: form.appSecret.trim(),
        verifyToken: form.verifyToken.trim(),
      });
      if (result?.webhookUrl && result.connectionId) {
        setWebhookUrl(result.webhookUrl);
        setConnectionId(result.connectionId);
      }
    } catch {
      // Keep the modal open so the user can retry after an error toast.
    }
  };

  const handleBindAndActivate = async () => {
    if (!connectionId || !onBindAndActivate) return;

    try {
      await onBindAndActivate(connectionId);
    } catch {
      // Keep the modal open so the user can retry after an error toast.
    }
  };

  const handlePrimaryAction = () => {
    if (isAwaitingActivation) {
      void handleBindAndActivate();
      return;
    }

    void handleSave();
  };

  const renderField = (field: SetupField) => (
    <div key={field.key} className="flex min-w-0 flex-col gap-1">
      <label
        htmlFor={`whatsapp-${field.key}`}
        className="font-sans text-sm font-medium text-grey-700 dark:text-true-white"
      >
        {field.label}
      </label>
      <Input
        id={`whatsapp-${field.key}`}
        type={field.secret ? 'password' : 'text'}
        value={form[field.key]}
        onChange={(event) => updateField(field.key, event.target.value)}
        placeholder={field.placeholder}
        autoComplete="off"
        spellCheck={false}
        className={inputClassName}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
      <DialogContent
        hideClose
        className="flex max-h-[90vh] min-h-0 w-full max-w-[565px] flex-col gap-0 overflow-hidden rounded-xl border-0 bg-white p-0 shadow-[0_24px_80px_rgba(15,23,42,0.18)] dark:bg-background"
      >
        <div className="relative min-h-0 flex-1 overflow-y-auto p-4 sm:px-8 sm:pt-8 sm:pb-5">
          <button
            type="button"
            aria-label={t('CLOSE') || 'Close'}
            onClick={handleClose}
            className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center text-[#6D6D6D] focus:outline-none dark:text-true-white"
          >
            <Xmark width={14} height={14} />
          </button>

          <div className="flex flex-col gap-4">
            <DialogTitle className="pr-6 font-sans text-2xl font-semibold leading-[120%] text-grey-700 dark:text-true-white">
              {t('INTEGRATE_WHATSAPP', { defaultValue: 'Integrate WhatsApp' })}
            </DialogTitle>

            <div className="flex items-start gap-2 rounded bg-[#FFFAEB] px-3 py-2.5">
              <InfoCircle
                width={13.333}
                height={13.333}
                color="#725111"
                strokeWidth={1.25}
                className="mt-0.5 shrink-0"
              />
              <p className="font-sans text-xs leading-[160%] text-[#725111]">
                {t('WHATSAPP_CONNECT_DESCRIPTION', {
                  defaultValue:
                    'Paste Meta Cloud API values below, then save and validate. A test number is fine while developing.',
                })}
              </p>
            </div>

            <ol className="flex flex-col gap-2">
              {steps.map((step, index) => (
                <li key={step.id} className="flex items-start gap-2.5">
                  <span
                    className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-black text-xs font-semibold leading-none text-white dark:bg-foreground dark:text-background"
                    aria-hidden
                  >
                    {index + 1}
                  </span>
                  <p className="font-sans text-sm leading-[150%] text-grey-700 dark:text-grey-300">
                    {step.content}
                  </p>
                </li>
              ))}
            </ol>

            <Button
              type="button"
              variant="outline"
              className={cn(pillButtonClassName, 'w-fit')}
              style={pillButtonStyle}
              onClick={() => window.open(META_DEVELOPER_URL, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="size-3.5" aria-hidden />
              {t('OPEN_META_DEVELOPER', { defaultValue: 'Open Meta Developer' })}
            </Button>

            <Accordion type="single" collapsible>
              <AccordionItem value="more-details" className="border-grey-100 dark:border-border">
                <AccordionTrigger className="py-2 text-sm font-semibold text-grey-700 hover:no-underline dark:text-true-white">
                  {t('WHATSAPP_MORE_DETAILS', { defaultValue: 'More details' })}
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-2 pb-2 text-sm leading-[150%] text-grey-700 dark:text-grey-300">
                    <p>
                      {t('WHATSAPP_MORE_DETAILS_NEED', {
                        defaultValue:
                          'You need a Meta Business Portfolio, developer account, WhatsApp-enabled app, WhatsApp Business Account, Cloud API phone number, and a published agent.',
                      })}
                    </p>
                    <p>
                      {t('WHATSAPP_MORE_DETAILS_AFTER', {
                        defaultValue:
                          'After saving, copy the webhook URL in Meta (WhatsApp → Configuration → Webhook), use the same verify token, subscribe to messages, then activate and test from another WhatsApp account.',
                      })}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{wabaPhoneFields.map(renderField)}</div>
            {accessTokenField ? renderField(accessTokenField) : null}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{metaAppFields.map(renderField)}</div>
            {verifyTokenField ? renderField(verifyTokenField) : null}

            {webhookUrl ? (
              <div className="flex min-w-0 flex-col gap-1">
                <label
                  htmlFor="whatsapp-webhook-url"
                  className="font-sans text-sm font-medium text-grey-700 dark:text-true-white"
                >
                  {t('WHATSAPP_WEBHOOK_URL', { defaultValue: 'Webhook URL' })}
                </label>
                <div className="flex gap-2">
                  <Input
                    id="whatsapp-webhook-url"
                    readOnly
                    value={webhookUrl}
                    className={inputClassName}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 shrink-0 gap-1.5 rounded-lg border-grey-100 px-3"
                    onClick={handleCopyWebhookUrl}
                  >
                    <Copy className="size-4" aria-hidden />
                    {t('COPY', { defaultValue: 'Copy' })}
                  </Button>
                </div>
                <p className="font-sans text-xs leading-[160%] text-grey-500 dark:text-grey-400">
                  {t('WHATSAPP_WEBHOOK_URL_HINT', {
                    defaultValue:
                      'Paste this URL in Meta (WhatsApp → Configuration → Webhook) with the same verify token.',
                  })}
                </p>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-grey-100 bg-white px-4 py-3 dark:border-border dark:bg-background sm:px-8">
          <Button
            type="button"
            variant="outline"
            className={cn(pillButtonClassName, 'w-[92px] px-0')}
            style={pillButtonStyle}
            disabled={isBusy}
            onClick={handleClose}
          >
            {t('CANCEL') || 'Cancel'}
          </Button>
          <Button
            type="button"
            className={cn(
              'h-10 min-w-[150px] gap-1.5 rounded-[62px] border-0 px-5 text-sm font-semibold text-white shadow-none',
              '!bg-black-base hover:!bg-grey-900',
              'disabled:!bg-grey-200 disabled:!text-grey-500 disabled:!opacity-100',
              'dark:!bg-[#3D3D3D] dark:hover:!bg-[#4F4F4F] dark:text-true-white',
              'disabled:dark:!bg-[#27272C] disabled:dark:!text-grey-500 disabled:dark:!opacity-100'
            )}
            disabled={
              isBusy ||
              (isAwaitingActivation ? !connectionId : !isWhatsAppConfigComplete(form))
            }
            onClick={handlePrimaryAction}
          >
            {isBusy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            {isAwaitingActivation
              ? t('BIND_AND_ACTIVATE', { defaultValue: 'Bind and Activate' })
              : t('SAVE_AND_VALIDATE', { defaultValue: 'Save and Validate' })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
